import { SHOW_NICHE_PAGES } from "@/config/features";
import type { AppLocale } from "@/i18n/constants";

/**
 * Landing pages per type of business. The copy speaks to each niche's usual
 * situation; it makes no claims about results, clients or numbers. Add a
 * niche here and it gets its page, route, sitemap entry and FAQ schema.
 */
export type NicheCopy = {
  /** Short name for chips and breadcrumbs, e.g. "Peluquerías". */
  name: string;
  seo: { title: string; description: string; keywords: string };
  hero: { title: string; lead: string };
  pains: { title: string; items: readonly string[] };
  plan: { title: string; lead: string; items: readonly { title: string; desc: string }[] };
  ideas: { title: string; lead: string; items: readonly string[] };
  faqs: readonly { question: string; answer: readonly string[] }[];
};

export type Niche = { id: string; path: string } & Record<AppLocale, NicheCopy>;

const ALL_NICHES: readonly Niche[] = [
  {
    id: "peluquerias",
    path: "/marketing-para-peluquerias",
    es: {
      name: "Peluquerías",
      seo: {
        title: "Marketing e Instagram para peluquerías — Más reservas | Lucentia Social",
        description:
          "Instagram, web y reservas online para peluquerías: un perfil que convierte, contenido con intención y una agenda que se llena sin vivir pendiente de WhatsApp.",
        keywords:
          "marketing para peluquerías, Instagram para peluquerías, community manager peluquería, reservas online peluquería, más clientes peluquería",
      },
      hero: {
        title: "Instagram y reservas para peluquerías que quieren la agenda llena",
        lead: "Tu trabajo ya se ve bien en fotos. Lo que falta es un sistema: un perfil que convierta, contenido con intención y reservas online para no depender de contestar WhatsApp entre cliente y cliente.",
      },
      pains: {
        title: "Lo que suele pasar en una peluquería",
        items: [
          "Publicás fotos de tus trabajos, pero los mensajes que llegan son preguntas de precio que no terminan en cita.",
          "Los huecos de última hora y las cancelaciones se quedan vacíos porque no hay una forma rápida de llenarlos.",
          "Contestás WhatsApp con las manos ocupadas y las consultas fuera de horario se enfrían.",
        ],
      },
      plan: {
        title: "Qué haría en tu peluquería",
        lead: "Siempre después de la auditoría: primero entiendo tu negocio, después propongo.",
        items: [
          { title: "Un perfil que vende", desc: "Bio clara, destacadas con servicios, precios orientativos y cómo reservar." },
          { title: "Contenido con intención", desc: "Antes y después, procesos, tendencias de temporada y opiniones reales, cada uno con su llamada a la reserva." },
          { title: "Reservas online 24/7", desc: "Enlazadas desde Instagram y Google, para que reserven sin esperar tu respuesta." },
          { title: "Huecos libres a la vista", desc: "Un formato fijo de historias para avisar de las citas disponibles de la semana." },
        ],
      },
      ideas: {
        title: "Ideas de contenido para empezar esta semana",
        lead: "Cinco formatos que funcionan en peluquería porque muestran tu trabajo y llevan a reservar.",
        items: [
          "Antes y después de un cambio de color",
          "El proceso de un balayage en 30 segundos",
          "Qué servicio pedir según tu tipo de pelo",
          "Mitos sobre el cuidado del color",
          "Huecos libres de esta semana (historia)",
        ],
      },
      faqs: [
        {
          question: "¿Cómo puede una peluquería conseguir más clientes con Instagram?",
          answer: [
            "Con un perfil que deje claro qué servicios ofrece, dónde está y cómo reservar, y con contenido que muestre resultados reales (antes y después, procesos) terminado en una llamada a la acción.",
            "Lo que más cambia los resultados es conectar Instagram con un sistema de reservas, para que quien se interesa pueda reservar en el momento.",
          ],
        },
        {
          question: "¿Qué publicar en el Instagram de una peluquería?",
          answer: [
            "Antes y después, procesos cortos en reels, consejos de cuidado, tendencias de temporada y opiniones de clientes. Cada publicación debería tener un objetivo: atraer, generar confianza o llevar a la reserva.",
          ],
        },
      ],
    },
    en: {
      name: "Hair salons",
      seo: {
        title: "Marketing and Instagram for hair salons — More bookings | Lucentia Social",
        description:
          "Instagram, website and online booking for hair salons: a profile that converts, purposeful content and a full diary without living on WhatsApp.",
        keywords: "marketing for hair salons, Instagram for hair salons, salon social media, online booking salon",
      },
      hero: {
        title: "Instagram and bookings for hair salons that want a full diary",
        lead: "Your work already looks good in photos. What's missing is a system: a profile that converts, purposeful content and online booking so you don't depend on answering WhatsApp between clients.",
      },
      pains: {
        title: "What usually happens in a hair salon",
        items: [
          "You post photos of your work, but the messages you get are price questions that never turn into appointments.",
          "Last-minute gaps and cancellations stay empty because there's no quick way to fill them.",
          "You answer WhatsApp with your hands full, and out-of-hours enquiries go cold.",
        ],
      },
      plan: {
        title: "What I'd do in your salon",
        lead: "Always after the audit: first I understand your business, then I propose.",
        items: [
          { title: "A profile that sells", desc: "A clear bio, highlights with services, guide prices and how to book." },
          { title: "Purposeful content", desc: "Before and after, processes, seasonal trends and real reviews, each with a call to book." },
          { title: "24/7 online booking", desc: "Linked from Instagram and Google, so clients book without waiting for your reply." },
          { title: "Free slots on show", desc: "A regular story format to announce the week's available appointments." },
        ],
      },
      ideas: {
        title: "Content ideas to start this week",
        lead: "Five formats that work for salons because they show your work and lead to bookings.",
        items: [
          "Before and after a colour change",
          "A balayage process in 30 seconds",
          "Which service to ask for by hair type",
          "Myths about colour care",
          "This week's free slots (story)",
        ],
      },
      faqs: [
        {
          question: "How can a hair salon get more clients with Instagram?",
          answer: [
            "With a profile that makes clear what services it offers, where it is and how to book, and with content that shows real results (before and after, processes) ending in a call to action.",
            "What changes results the most is connecting Instagram to a booking system, so interested people can book right away.",
          ],
        },
        {
          question: "What should a hair salon post on Instagram?",
          answer: [
            "Before and after, short process reels, care tips, seasonal trends and client reviews. Every post should have a goal: attract, build trust or lead to a booking.",
          ],
        },
      ],
    },
  },
  {
    id: "estetica",
    path: "/marketing-para-centros-de-estetica",
    es: {
      name: "Centros de estética",
      seo: {
        title: "Marketing e Instagram para centros de estética | Lucentia Social",
        description:
          "Instagram, web y reservas online para centros de estética: contenido que educa y genera confianza, y reservas sin ida y vuelta de mensajes.",
        keywords:
          "marketing para centros de estética, Instagram para centros de estética, community manager estética, reservas online estética",
      },
      hero: {
        title: "Instagram y reservas para centros de estética",
        lead: "Tus tratamientos generan dudas antes de reservar. Un buen sistema las responde por vos: perfil claro, contenido que educa y genera confianza, y reservas online sin ida y vuelta de mensajes.",
      },
      pains: {
        title: "Lo que suele pasar en un centro de estética",
        items: [
          "Cada tratamiento genera las mismas preguntas por mensaje: cuánto dura, si duele, cuántas sesiones hacen falta.",
          "Publicás promociones, pero no se nota en la agenda.",
          "Quien llega por primera vez no sabe qué tratamiento elegir y se queda en el «lo pienso».",
        ],
      },
      plan: {
        title: "Qué haría en tu centro de estética",
        lead: "Siempre después de la auditoría: primero entiendo tu negocio, después propongo.",
        items: [
          { title: "Dudas resueltas antes del mensaje", desc: "Perfil y destacadas que responden las preguntas frecuentes de cada tratamiento." },
          { title: "Contenido que educa", desc: "Qué es cada tratamiento, para quién es, cómo es una sesión y qué cuidados hacen falta." },
          { title: "Confianza visible", desc: "Resultados reales (con permiso) y opiniones de clientes en el lugar donde se decide." },
          { title: "Reservas y recordatorios", desc: "Reservas online y recordatorios automáticos para reducir mensajes y ausencias." },
        ],
      },
      ideas: {
        title: "Ideas de contenido para empezar esta semana",
        lead: "Cinco formatos que convierten dudas en reservas.",
        items: [
          "Así es tu primera sesión de un tratamiento",
          "Las 3 preguntas que siempre nos hacen",
          "Qué tratamiento elegir según tu objetivo",
          "Cuidados antes y después de la sesión",
          "Testimonio real, con permiso del cliente",
        ],
      },
      faqs: [
        {
          question: "¿Cómo conseguir más clientes para un centro de estética?",
          answer: [
            "Respondiendo las dudas antes de que aparezcan: un perfil y una web que expliquen cada tratamiento, contenido que eduque y opiniones reales que generen confianza.",
            "Y facilitando el último paso con reservas online, para que quien está decidido no tenga que esperar una respuesta.",
          ],
        },
        {
          question: "¿Qué contenido funciona en Instagram para un centro de estética?",
          answer: [
            "El que educa y da confianza: cómo es una sesión, para quién es cada tratamiento, cuidados, resultados reales con permiso y opiniones. Las promociones funcionan mejor cuando ya hay confianza construida.",
          ],
        },
      ],
    },
    en: {
      name: "Beauty centres",
      seo: {
        title: "Marketing and Instagram for beauty centres | Lucentia Social",
        description:
          "Instagram, website and online booking for beauty centres: content that educates and builds trust, and bookings without back-and-forth messages.",
        keywords: "marketing for beauty centres, Instagram for beauty salons, beauty social media, online booking beauty",
      },
      hero: {
        title: "Instagram and bookings for beauty centres",
        lead: "Your treatments raise questions before anyone books. A good system answers them for you: a clear profile, content that educates and builds trust, and online booking without back-and-forth messages.",
      },
      pains: {
        title: "What usually happens in a beauty centre",
        items: [
          "Every treatment brings the same questions by message: how long it takes, whether it hurts, how many sessions are needed.",
          "You post promotions, but it doesn't show in the diary.",
          "First-time visitors don't know which treatment to choose and end up saying \"I'll think about it\".",
        ],
      },
      plan: {
        title: "What I'd do in your beauty centre",
        lead: "Always after the audit: first I understand your business, then I propose.",
        items: [
          { title: "Questions answered before the message", desc: "A profile and highlights that answer each treatment's frequent questions." },
          { title: "Content that educates", desc: "What each treatment is, who it's for, what a session is like and the aftercare." },
          { title: "Visible trust", desc: "Real results (with permission) and client reviews where people decide." },
          { title: "Bookings and reminders", desc: "Online booking and automatic reminders to cut messages and no-shows." },
        ],
      },
      ideas: {
        title: "Content ideas to start this week",
        lead: "Five formats that turn doubts into bookings.",
        items: [
          "What your first treatment session is like",
          "The 3 questions we always get",
          "Which treatment to choose for your goal",
          "Before and after care",
          "A real testimonial, with the client's permission",
        ],
      },
      faqs: [
        {
          question: "How do you get more clients for a beauty centre?",
          answer: [
            "By answering questions before they come up: a profile and website that explain each treatment, content that educates and real reviews that build trust.",
            "And by making the last step easy with online booking, so people who've decided don't have to wait for a reply.",
          ],
        },
        {
          question: "What content works on Instagram for a beauty centre?",
          answer: [
            "Content that educates and builds trust: what a session is like, who each treatment is for, aftercare, real results with permission and reviews. Promotions work better once trust is built.",
          ],
        },
      ],
    },
  },
  {
    id: "fisioterapia",
    path: "/marketing-para-fisioterapeutas",
    es: {
      name: "Fisioterapeutas",
      seo: {
        title: "Marketing, web e Instagram para fisioterapeutas | Lucentia Social",
        description:
          "Web, Google Business Profile, Instagram y reservas online para fisioterapeutas: que te encuentren cuando buscan, confíen en vos y pidan cita sin esperar.",
        keywords:
          "marketing para fisioterapeutas, Instagram para fisioterapeutas, web para fisioterapeutas, más pacientes fisioterapia, reservas online fisioterapia",
      },
      hero: {
        title: "Web e Instagram para fisioterapeutas que quieren más pacientes",
        lead: "Tus pacientes te buscan cuando algo les duele, muchas veces en Google y fuera de horario. Un sistema digital hace que te encuentren, confíen en vos y pidan cita sin esperar a que contestes.",
      },
      pains: {
        title: "Lo que suele pasar en una consulta de fisioterapia",
        items: [
          "Los pacientes llegan casi siempre por recomendación y no sabés cómo atraer pacientes nuevos.",
          "Te cuesta saber qué publicar sin caer en contenido técnico que nadie entiende.",
          "Las consultas llegan a cualquier hora y entre sesión y sesión no podés contestar.",
        ],
      },
      plan: {
        title: "Qué haría en tu consulta",
        lead: "Siempre después de la auditoría: primero entiendo tu negocio, después propongo.",
        items: [
          { title: "Que te encuentren", desc: "Google Business Profile y web optimizados para aparecer cuando buscan fisioterapia cerca." },
          { title: "Contenido sin tecnicismos", desc: "Dolencias frecuentes, ejercicios simples y cómo es una primera sesión, explicado para pacientes." },
          { title: "Un perfil que da confianza", desc: "Especialidades, ubicación, horarios y cómo pedir cita, a la vista." },
          { title: "Agenda que se organiza sola", desc: "Reservas online y recordatorios automáticos." },
        ],
      },
      ideas: {
        title: "Ideas de contenido para empezar esta semana",
        lead: "Cinco formatos que generan confianza sin tecnicismos.",
        items: [
          "Cómo es tu primera sesión de fisioterapia",
          "Un ejercicio simple para la espalda en la oficina",
          "Mitos sobre el dolor de rodilla",
          "Cuándo conviene ir al fisio",
          "Un día en la consulta",
        ],
      },
      faqs: [
        {
          question: "¿Cómo puede un fisioterapeuta conseguir más pacientes?",
          answer: [
            "Siendo fácil de encontrar y de elegir: una ficha de Google Business Profile completa, una web clara con especialidades y reservas online, y contenido que explique sin tecnicismos cómo puede ayudar.",
            "Las recomendaciones siguen siendo clave; un buen perfil digital hace que quien recomienda tenga a dónde mandar a la otra persona.",
          ],
        },
        {
          question: "¿Le sirve Instagram a una clínica de fisioterapia?",
          answer: [
            "Sí, sobre todo para generar confianza: mostrar cómo se trabaja, explicar dolencias frecuentes y responder dudas. Funciona mejor conectado con una web y un sistema de reservas, para que el interés termine en una cita.",
          ],
        },
      ],
    },
    en: {
      name: "Physiotherapists",
      seo: {
        title: "Marketing, website and Instagram for physiotherapists | Lucentia Social",
        description:
          "Website, Google Business Profile, Instagram and online booking for physiotherapists: get found when people search, earn their trust and let them book without waiting.",
        keywords: "marketing for physiotherapists, Instagram for physiotherapists, physiotherapy website, more patients physio",
      },
      hero: {
        title: "Website and Instagram for physiotherapists who want more patients",
        lead: "Patients look for you when something hurts, often on Google and outside working hours. A digital system helps them find you, trust you and book without waiting for your reply.",
      },
      pains: {
        title: "What usually happens in a physiotherapy practice",
        items: [
          "Patients nearly always come by recommendation and you don't know how to attract new ones.",
          "It's hard to know what to post without slipping into technical content nobody understands.",
          "Enquiries arrive at any hour and you can't answer between sessions.",
        ],
      },
      plan: {
        title: "What I'd do in your practice",
        lead: "Always after the audit: first I understand your business, then I propose.",
        items: [
          { title: "Get found", desc: "An optimised Google Business Profile and website so you show up when people search for physio nearby." },
          { title: "Content without jargon", desc: "Common conditions, simple exercises and what a first session is like, explained for patients." },
          { title: "A profile that builds trust", desc: "Specialities, location, hours and how to book, in plain sight." },
          { title: "A diary that organises itself", desc: "Online booking and automatic reminders." },
        ],
      },
      ideas: {
        title: "Content ideas to start this week",
        lead: "Five formats that build trust without jargon.",
        items: [
          "What your first physio session is like",
          "One simple back exercise for office workers",
          "Myths about knee pain",
          "When it's worth seeing a physio",
          "A day at the practice",
        ],
      },
      faqs: [
        {
          question: "How can a physiotherapist get more patients?",
          answer: [
            "By being easy to find and to choose: a complete Google Business Profile, a clear website with specialities and online booking, and content that explains without jargon how you can help.",
            "Recommendations are still key; a good digital presence gives the people who recommend you somewhere to send others.",
          ],
        },
        {
          question: "Is Instagram useful for a physiotherapy clinic?",
          answer: [
            "Yes, above all to build trust: showing how you work, explaining common conditions and answering questions. It works best connected to a website and a booking system, so interest turns into an appointment.",
          ],
        },
      ],
    },
  },
];

/** Published niches: empty while SHOW_NICHE_PAGES is off. */
export const NICHES: readonly Niche[] = SHOW_NICHE_PAGES ? ALL_NICHES : [];

export const NICHE_PATHS: readonly string[] = NICHES.map((niche) => niche.path);

export function nicheByPath(path: string): Niche | undefined {
  return NICHES.find((niche) => niche.path === path);
}
